import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { mailbox } from "../../api/mailbox";
import type { SendInput } from "../../api/mailbox";

// 寫信時的草稿自動儲存(規格 §2.6「草稿自動儲存」)。
//
// - **有變動、停手 10 秒**才存(一直在打字就一直往後延);存好之後內容沒再變就不再存。
// - **同時只存一次**:存的途中又到時間,不開第二個請求 —— 記下來,等這次回來再過 10 秒存最新的那一版。
// - **草稿不帶附件**(規格 §2.3):上傳的檔與轉寄的原附件都只在寄出時帶上;附件變了也不算「要存」。
// - 存到雲端 `INBOX.Drafts`;第二版起帶上一版的 `draft_id`,後端存新的、刪舊的。
// - 寄出:先 `settle()`(停掉計時、等正在存的那次回來)拿到草稿編號交給 `/send`,後端寄完刪草稿。
// - 放棄:`discard()` 刪掉存過的草稿。之後不再存。
// - 離開頁面(關分頁、重新整理)前,**只有還沒存到的變動**才請瀏覽器詢問。
// - **卸載(站內換到別頁)時**有還沒存到的變動、而且沒停用:馬上存一次(不等 10 秒)。
//   正在存的話照樣不開第二個,等那次回來再存最新的。`settle()` / `discard()` 之後不做。
// - React StrictMode(開發模式)會「掛上 → 卸下 → 再掛上」一次:再掛上時把狀態恢復,
//   不可以因為那次假卸下就永遠不存。

/** 停手多久才存。 */
export const DRAFT_IDLE_MS = 10_000;

export type DraftStatus = "idle" | "saving" | "saved" | "error";

export interface DraftAutosave {
  /** 雲端那份草稿的 Message-ID;還沒存過是 null。 */
  draftId: string | null;
  status: DraftStatus;
  /** 有動過、而且最新的內容還沒存到。 */
  hasUnsaved: boolean;
  /** 停止自動儲存並等正在存的那次回來;回傳草稿編號(寄出時交給 `/send`)。 */
  settle: () => Promise<string | null>;
  /** 寄出失敗:恢復自動儲存。 */
  resume: () => void;
  /** 放棄這封信:停止自動儲存、刪掉存過的草稿。 */
  discard: () => Promise<void>;
}

/** 內容的指紋:會存進草稿的欄位(不含附件 —— 草稿不帶附件)。 */
function fingerprint(fields: SendInput): string {
  return JSON.stringify([
    fields.to,
    fields.cc,
    fields.bcc,
    fields.subject,
    fields.body,
    fields.replyToId ?? null,
    fields.forwardId ?? null,
  ]);
}

export function useDraftAutosave(fields: SendInput, dirty: boolean, enabled = true): DraftAutosave {
  const key = useMemo(() => fingerprint(fields), [fields]);
  const latest = useRef({ fields, key, dirty });
  latest.current = { fields, key, dirty };

  const [draftId, setDraftId] = useState<string | null>(null);
  const [status, setStatus] = useState<DraftStatus>("idle");
  const [savedKey, setSavedKey] = useState<string | null>(null);

  const draftRef = useRef<string | null>(null);
  const savedKeyRef = useRef<string | null>(null);
  const timer = useRef<number | null>(null);
  const inFlight = useRef<Promise<void> | null>(null);
  const queued = useRef(false);
  /** `settle()` / `discard()` 之後:不再存(寄出失敗時 `resume()` 才恢復)。 */
  const finished = useRef(false);
  const mounted = useRef(true);
  /** 卸載時補存中:元件已經不在了,但這一次(與排在後面的那一次)照樣要存。 */
  const flushing = useRef(false);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;
  // `arm` 與 `save` 互相呼叫;用 ref 拿到最新的那一份
  const armRef = useRef<() => void>(() => {});
  const saveRef = useRef<() => void>(() => {});

  const clearTimer = () => {
    if (timer.current !== null) {
      window.clearTimeout(timer.current);
      timer.current = null;
    }
  };

  const needsSave = () =>
    !finished.current &&
    (mounted.current || flushing.current) &&
    latest.current.dirty &&
    latest.current.key !== savedKeyRef.current;

  const save = useCallback(() => {
    timer.current = null;
    if (!needsSave()) return;
    if (inFlight.current) {
      queued.current = true; // 不開第二個;這次回來再排
      return;
    }
    const { fields: snapshot, key: snapshotKey } = latest.current;
    if (mounted.current) setStatus("saving");
    inFlight.current = mailbox
      .saveDraft(snapshot, draftRef.current)
      .then((got) => {
        const id = typeof got?.draft_message_id === "string" ? got.draft_message_id : null;
        if (id) draftRef.current = id;
        savedKeyRef.current = snapshotKey;
        if (mounted.current) {
          setDraftId(draftRef.current);
          setSavedKey(snapshotKey);
          setStatus("saved");
        }
      })
      .catch(() => {
        // 存不成(雲端連不上、密碼被拒)不打擾;內容再變時會再試,離開頁面時照樣會問
        if (mounted.current) setStatus("error");
      })
      .finally(() => {
        inFlight.current = null;
        if (queued.current) {
          queued.current = false;
          // 還在頁面上:再等 10 秒;已經卸載(補存中):馬上存最新的那一版
          if (mounted.current) armRef.current();
          else saveRef.current();
        }
      });
  }, []);
  saveRef.current = save;

  useEffect(() => {
    mounted.current = true;
    flushing.current = false;
    return () => {
      mounted.current = false;
      if (timer.current !== null) {
        window.clearTimeout(timer.current);
        timer.current = null;
      }
      // 換到別頁:還沒存到的變動馬上存一次(StrictMode 的假卸下也會走到這裡,多存一次無妨)
      if (!finished.current && enabledRef.current) {
        flushing.current = true;
        saveRef.current();
      }
    };
  }, []);

  const arm = useCallback(() => {
    clearTimer();
    if (!enabled || !mounted.current || !needsSave()) return;
    timer.current = window.setTimeout(save, DRAFT_IDLE_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, save]);
  armRef.current = arm;

  // 內容一變就重新計時(停手 10 秒才存)
  useEffect(() => {
    arm();
  }, [key, dirty, arm]);

  const hasUnsaved = dirty && key !== savedKey;

  // 關分頁 / 重新整理:只有還沒存到的變動才問
  useEffect(() => {
    if (!hasUnsaved) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasUnsaved]);

  const settle = useCallback(async () => {
    finished.current = true;
    flushing.current = false;
    queued.current = false;
    clearTimer();
    if (inFlight.current) await inFlight.current;
    return draftRef.current;
  }, []);

  const resume = useCallback(() => {
    if (!mounted.current) return;
    finished.current = false;
    armRef.current();
  }, []);

  const discard = useCallback(async () => {
    const id = await settle();
    if (!id) return;
    draftRef.current = null;
    if (mounted.current) setDraftId(null);
    try {
      await mailbox.discardDraft(id);
    } catch {
      // 刪不掉(雲端連不上):草稿留在草稿匣,本人在那裡看得到、刪得掉
    }
  }, [settle]);

  return { draftId, status, hasUnsaved, settle, resume, discard };
}
